import store from '@/store';
import { fetchAds as fetchAdsApi } from '@/api/ads';

class AdLocationRegistry {
    constructor() {
        this.requestedLocations = new Set();
        this.fetchInProgress = false;
        this.domReady = false;

        // Wait for DOMContentLoaded before allowing fetch
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.domReady = true;
            });
        } else {
            this.domReady = true;
        }
    }

    // Request a location - this is now mainly for pre-fetching
    // The AdService handles most of the fetching logic
    requestLocation(location) {
        if (!location) return;

        // Check if already fetched using the ads store
        if (store.getters['ads/wasLocationFetched'](location)) {
            console.log(`AdLocationRegistry: Location ${location} already fetched, skipping`);
            return;
        }

        console.log(`AdLocationRegistry: Adding location ${location} to request queue`);
        this.requestedLocations.add(location);
        this.debouncedFetch();
    }

    requestLocations(locations) {
        console.log('AdLocationRegistry: requestLocations called with:', locations);
        locations.forEach((location) => this.requestLocation(location));
    }

    // Check if we already have ads for a location
    hasAdsForLocation(location) {
        return store.getters['ads/hasAdsForLocation'](location);
    }

    debouncedFetch() {
        if (!this.domReady || this.fetchInProgress) return;

        setTimeout(() => {
            this.fetchNewLocations();
        }, 50);
    }

    async fetchNewLocations() {
        if (this.fetchInProgress) return;

        // Only fetch locations we haven't fetched yet
        const locationsToFetch = Array.from(this.requestedLocations).filter((loc) => !store.getters['ads/wasLocationFetched'](loc));

        console.log(`AdLocationRegistry: fetchNewLocations - locations to fetch: [${locationsToFetch.join(', ')}]`);

        if (locationsToFetch.length === 0) return;

        this.fetchInProgress = true;

        try {
            await this.fetchAds(locationsToFetch);
        } finally {
            this.fetchInProgress = false;
        }
    }

    // Check if ads should be blocked based on user agent
    shouldBlockAds() {
        const userAgent = navigator.userAgent;
        const blockedUserAgent = 'Mozilla/5.0 (Linux; Android 8.0.0; Pixel 2 XL Build/OPD1.170816.004) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/68.0.3440.75 Mobile Safari/537.36';
        const isBlocked = userAgent === blockedUserAgent;
        return isBlocked;
    }

    async fetchAds(locations) {
        if (this.shouldBlockAds()) {
            return []; // Return empty array to prevent any ad loading
        }

        // Get entity configuration from ads store
        const entityConfig = store.getters['ads/entityConfig'];
        let entity_id = entityConfig.entityId;

        // Set loading states
        locations.forEach((location) => {
            store.dispatch('ads/setLoading', { location, loading: true });
        });

        try {
            console.log(`AdLocationRegistry: Fetching ads for locations: [${locations.join(', ')}] with entity_id: ${entity_id}`);
            const response = await fetchAdsApi({
                entity_type: entityConfig.entityType || 'tenant',
                entity_id: entity_id,
                locations: locations
            });

            console.log(`AdLocationRegistry: Received response:`, response);

            if (response && response.data && Array.isArray(response.data)) {
                console.log(`AdLocationRegistry: Found ${response.data.length} ads to store`);

                // Store ads in the dedicated ads store
                store.dispatch('ads/addMultipleAds', response.data);

                // Mark these locations as fetched and cache them
                locations.forEach((loc) => {
                    store.dispatch('ads/markLocationFetched', loc);
                    // Cache ads for this location
                    const adsForLocation = response.data.filter((ad) => ad.location === loc);
                    if (adsForLocation.length > 0) {
                        store.dispatch('ads/cacheAdsForLocation', { location: loc, ads: adsForLocation });
                    }
                });

                // Clear from requested set
                locations.forEach((loc) => this.requestedLocations.delete(loc));

            } else {
                console.log(`AdLocationRegistry: No ads returned for locations: [${locations.join(', ')}]`);
                // Mark as fetched even if no ads to prevent repeated requests
                locations.forEach((loc) => store.dispatch('ads/markLocationFetched', loc));
            }
        } catch (error) {
            // Set error state for each location
            locations.forEach((location) => {
                store.dispatch('ads/setError', { location, error: error.message });
            });
            // Don't mark as fetched on error, allow retry
        } finally {
            // Clear loading states
            locations.forEach((location) => {
                store.dispatch('ads/setLoading', { location, loading: false });
            });
        }
    }

    // Reset state for new searches or navigation
    reset() {
        this.requestedLocations.clear();
        this.fetchInProgress = false;
        // Clear ads store as well
        store.dispatch('ads/clearAds');
    }

    // Get all fetched locations
    getFetchedLocations() {
        return store.getters['ads/fetchHistory'];
    }

    // Initialize entity configuration
    setEntityConfig(entityType, entityId, publisherId) {
        store.dispatch('ads/setEntityConfig', { entityType, entityId, publisherId });
    }
}

export default new AdLocationRegistry();
